import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { type CliLoginRequest, decideCliLoginRequest, getCliLoginRequest } from "@/lib/api";
import { formatDateTime } from "@/lib/format-datetime";

type Decision = "approved" | "denied";

/** `izanagi login` が開くページ。CLI に表示されたコードと一致するか確認してから承認させる。 */
export function CliAuthorizePage() {
	const [params] = useSearchParams();
	const code = params.get("code") ?? "";
	const [request, setRequest] = useState<CliLoginRequest | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [decision, setDecision] = useState<Decision | null>(null);
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		if (code === "") {
			setError("URL にログインコードが含まれていません。izanagi login をやり直してください。");
			return;
		}
		getCliLoginRequest(code)
			.then(setRequest)
			.catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
	}, [code]);

	async function decide(approve: boolean) {
		setSubmitting(true);
		setError(null);
		try {
			await decideCliLoginRequest(code, approve);
			setDecision(approve ? "approved" : "denied");
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<div className="grid min-h-screen place-items-center bg-surface-2 p-6 font-sans">
			<div
				data-testid="cli-authorize-page"
				className="flex w-full max-w-[400px] flex-col gap-5 rounded-5 bg-surface-1 p-8 shadow-card"
			>
				<h1 className="m-0 text-display font-semibold text-fg-1">Izanagi CLI のログイン</h1>
				{decision !== null ? (
					<p className="m-0 text-ui text-fg-2">
						{decision === "approved"
							? "承認しました。ターミナルに戻ってください。このページは閉じてかまいません。"
							: "拒否しました。このページは閉じてかまいません。"}
					</p>
				) : (
					request !== null && (
						<RequestDetails request={request} submitting={submitting} onDecide={decide} />
					)
				)}
				{error !== null && (
					<div className="rounded-3 bg-danger-subtle px-2.5 py-2 text-caption text-danger shadow-[0_0_0_1px_var(--danger-line)]">
						{error}
					</div>
				)}
			</div>
		</div>
	);
}

function RequestDetails({
	request,
	submitting,
	onDecide,
}: {
	request: CliLoginRequest;
	submitting: boolean;
	onDecide: (approve: boolean) => void;
}) {
	if (request.status !== "pending") {
		const message =
			request.status === "expired"
				? "このログインリクエストは有効期限が切れています。izanagi login をやり直してください。"
				: "このログインリクエストはすでに処理されています。";
		return <p className="m-0 text-ui text-fg-2">{message}</p>;
	}

	return (
		<>
			<p className="m-0 text-ui text-fg-2">
				<span className="text-fg-1">{request.clientName}</span> の CLI
				があなたのアカウントでのログインを求めています ({formatDateTime(request.createdAt)})。
			</p>
			<div
				data-testid="cli-user-code"
				className="rounded-3 bg-surface-2 py-3 text-center font-mono text-heading font-semibold text-fg-1"
			>
				{request.userCode}
			</div>
			<p className="m-0 text-caption text-warning">
				ターミナルに表示されたコードと一致し、自分で izanagi login
				を実行した場合のみ承認してください。 承認すると、その CLI
				はあなたとしてチャットやエージェントを操作できます。
			</p>
			<div className="flex justify-end gap-2">
				<button
					type="button"
					disabled={submitting}
					onClick={() => onDecide(false)}
					className="h-9 cursor-pointer rounded-3 bg-transparent px-4 text-ui text-fg-1 shadow-ring transition-[background-color,box-shadow] hover:bg-overlay-hover hover:shadow-ring-strong disabled:cursor-not-allowed"
				>
					拒否
				</button>
				<button
					type="button"
					disabled={submitting}
					onClick={() => onDecide(true)}
					className="h-9 cursor-pointer rounded-3 bg-accent-solid px-4 text-ui font-medium text-on-accent-solid hover:bg-accent-solid-hover disabled:cursor-not-allowed"
				>
					承認
				</button>
			</div>
		</>
	);
}
